import { Component, OnInit, Inject, NgZone } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatTableDataSource } from '@angular/material';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { API_BASE_URL } from '../../../environments/environment';
import { Router } from '@angular/router';
import moment from 'moment';

export interface DialogData {
  any
}

/**
 * APIの戻り値1行分
 * emp（勤務）に対して 休み登録 × 代勤登録 のLEFT JOINで直積になっているため、
 * 同じempの情報が複数行にわたって重複している
 */
export interface DataConfirmRow {
  // emp（勤務）
  nameStaff: string;
  site: string;
  day: string;
  staffDailyCleaning: string;
  cdSite: string;
  sequenceNumber: string;
  cdContract: string;
  hours: string;
  isHoliday: string;
  startTime: string;
  endTime: string;
  // 休み登録
  holiId: string;
  holiSequenceNumber: string;
  holiStatus: string;
  holiCdSite: string;
  holiSite: string;
  holiIsNotShow: string;
  // 代勤登録
  opId: string;
  opSequenceNumber: string;
  opStartTime: string;
  opEndTime: string;
  opHours: string;
  opStatus: string;
  opCdSite: string;
  opSite: string;
  opIsNotShow: string;
}

/**
 * 休み登録
 */
export interface Holiday {
  id: string;
  sequenceNumber: string;
  status: string;
  cdSite: string;
  site: string;
  isNotShow: string;
}

/**
 * 代勤登録
 */
export interface Operation {
  id: string;
  sequenceNumber: string;
  startTime: string;
  endTime: string;
  hours: string;
  status: string;
  cdSite: string;
  site: string;
  isNotShow: string;
}

/**
 * 日付列
 */
export interface DayColumn {
  // YYYY-MM-DD
  day: string;
  // 表示用（7/21（火））
  label: string;
  // 曜日（0:日～6:土）
  week: number;
}

/**
 * 勤務（emp）の1日分
 */
export interface EmpCell {
  day: string;
  hours: string;
  isHoliday: string;
  startTime: string;
  endTime: string;
}

/**
 * 休み登録の行（物件＋枝番単位）
 */
export interface HolidayRow {
  key: string;
  site: string;
  cdSite: string;
  sequenceNumber: string;
  isNotShow: string;
  // 日付列と同じ並び
  cells: Holiday[][];
}

/**
 * 代勤登録の行（物件＋枝番単位）
 */
export interface OperationRow {
  key: string;
  site: string;
  cdSite: string;
  sequenceNumber: string;
  isNotShow: string;
  // 日付列と同じ並び
  cells: Operation[][];
}

/**
 * 勤務（emp）の行（物件＋枝番単位）
 * 配下に、確認してほしい休み登録・代勤登録をぶら下げる
 */
export interface EmpRow {
  key: string;
  site: string;
  cdSite: string;
  sequenceNumber: string;
  cdContract: string;
  // 日付列と同じ並び（勤務なしはnull）
  cells: EmpCell[];
  // 日付列と同じ並び。この勤務に休み登録が必要かどうか
  // 'op'：代勤とかぶっているので必要／'holi'：他の行に休み登録があるので要確認／''：指摘なし
  needHoliday: string[];
  // 日付列と同じ並び。この画面から休み登録したかどうか（登録済みはグレーアウトして再登録させない）
  registered: boolean[];
  holidayRows: HolidayRow[];
  operationRows: OperationRow[];
}

/**
 * 清掃員単位のまとまり（表示の1ブロック）
 */
export interface StaffGroup {
  staffDailyCleaning: string;
  nameStaff: string;
  // 日付（列）。昇順
  columns: DayColumn[];
  // 担当物件ごとの勤務
  empRows: EmpRow[];
  // 確認が必要な休み登録の件数
  holidayCount: number;
  // 確認が必要な代勤登録の件数
  operationCount: number;
}

@Component({
  selector: 'app-data-confirm-dialog',
  templateUrl: './data-confirm-dialog.component.html',
  styleUrls: ['./data-confirm-dialog.component.less']
})
export class DataConfirmDialogComponent implements OnInit {

  public headers = new HttpHeaders({
    "X-LoginAccessKey": localStorage.getItem("accessKey")
  });

  // ローディング中の表示
  nowLoading = true;

  // 曜日
  weekDays = ['日', '月', '火', '水', '木', '金', '土'];

  // テーブルのカラム名
  columnDefs = [
    'cdStaff',
    'staff',
    'cdSite',
    'site',
    'cdContract',
    'nameCustomer'
  ];

  // 検索開始日（YYYY-MM-DD文字列。Datepicker利用時はDateが入る）
  startDate: any;

  // 検索終了日（YYYY-MM-DD文字列。Datepicker利用時はDateが入る）
  endDate: any;

  // 一覧（清掃員単位にまとめたもの）
  list: StaffGroup[] = [];

  // 件数（清掃員数）
  length = 0;

  // 確認が必要な休み登録の件数（全体）
  holidayCount = 0;

  // 確認が必要な代勤登録の件数（全体）
  operationCount = 0;

  constructor(private router: Router, private http: HttpClient, @Inject(MAT_DIALOG_DATA) private data: DialogData, private dialogRef: MatDialogRef<DataConfirmDialogComponent>, private ngZone: NgZone) { }

  ngOnInit() {
    this.setDefaultTerm();
    this.getData();
  }

  /**
   * 検索期間の初期値設定（当月1日～翌月末日）
   */
  private setDefaultTerm() {
    const today = new Date();
    this.startDate = this.formatDate(new Date(today.getFullYear(), today.getMonth(), 1));
    this.endDate = this.formatDate(new Date(today.getFullYear(), today.getMonth() + 2, 0));
  }

  goTo(comp, param){
    this.ngZone.run(()=>{
    this.router.navigate([comp, param])
    });
  }

  /**
   * 検索ボタンイベント
   */
  search() {
    const start = this.formatDate(this.startDate);
    const end = this.formatDate(this.endDate);

    if (!this.hasValue(start) || !this.hasValue(end)) {
      alert("検索開始日と検索終了日を入力してください");
      return;
    }
    if (start > end) {
      alert("検索開始日は検索終了日以前を指定してください");
      return;
    }

    this.getData();
  }

  /**
   * データ取得
   */
  getData() {
    this.nowLoading = true;

    this.list = [];
    this.length = 0;
    this.holidayCount = 0;
    this.operationCount = 0;

    // 検索期間
    const params = new HttpParams()
      .set('startDate', this.formatDate(this.startDate))
      .set('endDate', this.formatDate(this.endDate));

    this.http.get(
      API_BASE_URL + '/api/v1/contractDetail/dataConfirm',
      {headers: this.headers, params: params}
    )
    .subscribe(res => {
      const data = res as DataConfirmRow[];
      if (data.length) {
        this.list = this.groupByStaff(data);
        this.length = this.list.length;

        // 全体の件数
        this.list.forEach(staff => {
          this.holidayCount += staff.holidayCount;
          this.operationCount += staff.operationCount;
        });
      }
      // console.log(this.list)
      this.nowLoading = false;
    },
    error => {
      alert("認証に失敗しました");
      this.router.navigate(['/']);
    });
  }

  /**
   * 清掃員単位にまとめ、日付を列にした表示用データを作る
   *
   * SQL側で emp に対して 休み登録・代勤登録 をLEFT JOINしているため、
   * 休みn件 × 代勤m件 の直積で n×m 行に増えている。
   * 休み登録は holiId、代勤登録は opId で重複を除く。
   *
   * 清掃員
   *   └ 担当物件ごとの勤務（emp）        … 日付ごとに 勤務時間
   *       ├ 休み登録（物件＋枝番単位）    … 日付ごとに ステータス
   *       └ 代勤登録（物件＋枝番単位）    … 日付ごとに 時間帯・ステータス
   *
   * @param data APIの戻り値
   * @return 清掃員単位にまとめた一覧
   */
  private groupByStaff(data: DataConfirmRow[]): StaffGroup[] {
    // 取得順（清掃員→日付）を保つため配列とキー参照用の連想配列を併用
    const staffs: any[] = [];
    const staffIndex: { [key: string]: any } = {};

    data.forEach(row => {

      // 清掃員
      let staff = staffIndex[row.staffDailyCleaning];
      if (!staff) {
        staff = {
          staffDailyCleaning: row.staffDailyCleaning,
          nameStaff: row.nameStaff,
          dayIndex: {},
          days: [],
          empIndex: {},
          emps: [],
          holidayCount: 0,
          operationCount: 0
        };
        staffIndex[row.staffDailyCleaning] = staff;
        staffs.push(staff);
      }

      // 日付（列）
      if (!staff.dayIndex[row.day]) {
        staff.dayIndex[row.day] = true;
        staff.days.push(row.day);
      }

      // 勤務（物件＋枝番単位）
      const empKey = row.cdSite + '|' + row.sequenceNumber;
      let emp = staff.empIndex[empKey];
      if (!emp) {
        emp = {
          key: empKey,
          site: row.site,
          cdSite: row.cdSite,
          sequenceNumber: row.sequenceNumber,
          cdContract: row.cdContract,
          cellIndex: {},
          holiIndex: {},
          holis: [],
          opIndex: {},
          ops: []
        };
        staff.empIndex[empKey] = emp;
        staff.emps.push(emp);
      }

      // 勤務内容（empの一意キーにより1日1件）
      emp.cellIndex[row.day] = {
        day: row.day,
        hours: row.hours,
        isHoliday: row.isHoliday,
        startTime: row.startTime,
        endTime: row.endTime
      };

      // 休み登録（LEFT JOINで複製された分は holiId で除外）
      if (this.hasValue(row.holiId)) {
        const holiKey = row.holiCdSite + '|' + row.holiSequenceNumber;
        let holi = emp.holiIndex[holiKey];
        if (!holi) {
          holi = {
            key: holiKey,
            site: row.holiSite,
            cdSite: row.holiCdSite,
            sequenceNumber: row.holiSequenceNumber,
            isNotShow: row.holiIsNotShow,
            idIndex: {},
            cellIndex: {}
          };
          emp.holiIndex[holiKey] = holi;
          emp.holis.push(holi);
        }
        if (!holi.idIndex[row.holiId]) {
          holi.idIndex[row.holiId] = true;
          staff.holidayCount++;
          if (!holi.cellIndex[row.day]) {
            holi.cellIndex[row.day] = [];
          }
          holi.cellIndex[row.day].push({
            id: row.holiId,
            sequenceNumber: row.holiSequenceNumber,
            status: row.holiStatus,
            cdSite: row.holiCdSite,
            site: row.holiSite,
            isNotShow: row.holiIsNotShow
          });
        }
      }

      // 代勤登録（LEFT JOINで複製された分は opId で除外）
      if (this.hasValue(row.opId)) {
        const opKey = row.opCdSite + '|' + row.opSequenceNumber;
        let op = emp.opIndex[opKey];
        if (!op) {
          op = {
            key: opKey,
            site: row.opSite,
            cdSite: row.opCdSite,
            sequenceNumber: row.opSequenceNumber,
            isNotShow: row.opIsNotShow,
            idIndex: {},
            cellIndex: {}
          };
          emp.opIndex[opKey] = op;
          emp.ops.push(op);
        }
        if (!op.idIndex[row.opId]) {
          op.idIndex[row.opId] = true;
          staff.operationCount++;
          if (!op.cellIndex[row.day]) {
            op.cellIndex[row.day] = [];
          }
          op.cellIndex[row.day].push({
            id: row.opId,
            sequenceNumber: row.opSequenceNumber,
            startTime: row.opStartTime,
            endTime: row.opEndTime,
            hours: row.opHours,
            status: row.opStatus,
            cdSite: row.opCdSite,
            site: row.opSite,
            isNotShow: row.opIsNotShow
          });
        }
      }
    });

    // 日付列を確定し、列の並びに合わせてセルを展開
    return staffs.map(staff => {
      const days: string[] = staff.days.sort();

      return {
        staffDailyCleaning: staff.staffDailyCleaning,
        nameStaff: staff.nameStaff,
        holidayCount: staff.holidayCount,
        operationCount: staff.operationCount,
        columns: days.map(day => ({
          day: day,
          label: this.formatDayLabel(day),
          week: this.getWeekIndex(day)
        })),
        empRows: staff.emps.map(emp => ({
          key: emp.key,
          site: emp.site,
          cdSite: emp.cdSite,
          sequenceNumber: emp.sequenceNumber,
          cdContract: emp.cdContract,
          cells: days.map(day => emp.cellIndex[day] || null),
          needHoliday: days.map(day => this.judgeNeedHoliday(emp, day)),
          registered: days.map(() => false),
          holidayRows: emp.holis.map(holi => ({
            key: holi.key,
            site: holi.site,
            cdSite: holi.cdSite,
            sequenceNumber: holi.sequenceNumber,
            isNotShow: holi.isNotShow,
            cells: days.map(day => holi.cellIndex[day] || [])
          })),
          operationRows: emp.ops.map(op => ({
            key: op.key,
            site: op.site,
            cdSite: op.cdSite,
            sequenceNumber: op.sequenceNumber,
            isNotShow: op.isNotShow,
            cells: days.map(day => op.cellIndex[day] || [])
          }))
        }))
      };
    });
  }

  /**
   * 曜日取得
   *
   * @param day YYYY-MM-DD形式の日付
   * @return 曜日（日～土）
   */
  getWeekDay(day: string): string {
    const week = this.getWeekIndex(day);
    return week < 0 ? '' : this.weekDays[week];
  }

  /**
   * 曜日番号取得
   *
   * @param day YYYY-MM-DD形式の日付
   * @return 0:日～6:土（日付なしは-1）
   */
  private getWeekIndex(day: string): number {
    if (!this.hasValue(day)) {
      return -1;
    }
    return new Date(day.substring(0, 10).replace(/-/g, '/')).getDay();
  }

  /**
   * 休み登録ボタンイベント
   *
   * @param staff 清掃員
   * @param emp 勤務（物件＋枝番）
   * @param i 日付列の位置（staff.columns・emp.cellsと同じ並び）
   */
  async registerHoliday(staff: StaffGroup, emp: EmpRow, i: number) {
    // 二重登録の防止（連打・disabled以外の経路対策）
    if (emp.registered[i]) {
      return;
    }

    this.nowLoading = true;

    // YYYY-MM-DD形式の日付
    const day = staff.columns[i].day;

    // その日の勤務内容（勤務時間の引き継ぎに使用。勤務なしはnull）
    const cell: EmpCell = emp.cells[i];

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'X-LoginAccessKey': localStorage.getItem('accessKey')
      })
    };

    const note = '要確認画面にて休み登録'

    let workingHours = null
    if (cell && cell.hours) {
      workingHours = cell.hours
    }

    // 登録の API（休み登録ダイアログと同じ）は、種別・曜日・時間帯を日ごと（target）に受け取る
    const list = [{
      sequenceNumber: emp.sequenceNumber,
      cdSite: emp.cdSite,
      staffDailyCleaning: staff.staffDailyCleaning,
      nameSite: emp.site,
      target: [{
        day,
        week: this.weekDays[staff.columns[i].week],
        status: 0, // 休み固定
        workingHours,
        start: cell ? cell.startTime : null,
        end: cell ? cell.endTime : null,
        paidHolidayUsageTime: null,
        shortHours: null
      }]
    }]

    const saveData = {
      daysSelected: [day],
      note,
      note2: '', // 録音用メモ
      numberMobilePhone: '',
      numberPhone: '',
      list,
      kana: ''
    }

    const data = await this.getStatus(emp.sequenceNumber, staff.staffDailyCleaning, emp.cdSite, day)
    if (data && data['updateUser']) {
      this.getData();
      const msg = `登録者：${data['updateUser']}（${moment(data['updateDate']).format('M/D HH:mm')}）`
      alert('確認している間にすでに「休み登録」がされました。データを再取得します。\n' + msg)
      return
    }

    // 休み登録ダイアログと同じ API。userId・isHoliday は必須で、本文は { holiday, exchange } の形で送る
    // （前はどちらも送っておらず、サーバーが受け取りの時点で落ちて、休みは登録されずログにも残らなかった）
    // 休み登録として送るので、ログ画面に「休み登録」が残り、休み登録のメールも飛ぶ。RP のタスクは3種類とも立てない
    this.http.post(
      API_BASE_URL + '/api/v1/status?'
      + 'isRecord=' + false // 録音タスク登録有無
      + '&isReturnTask=' + false // 復帰確認タスク登録有無
      + '&isUrgentTask=' + false // 翌営業日以内の休みの RP メインタスク登録有無
      + '&staffName=' + encodeURIComponent(staff.nameStaff || '')
      + '&staffDailyCleaning=' + staff.staffDailyCleaning
      + '&userId=' + localStorage.getItem('uid') // ログの登録者（休み登録ダイアログと同じ uid）
      + '&isHoliday=' + true, // 休み登録：true、振替：false
      { holiday: saveData, exchange: null },
      httpOptions
    ).subscribe(res => {
      // 休み登録はここで完了。画面を読み込み直さず、このセルだけグレーアウトする
      emp.registered[i] = true;

      // tbl_operation_noteに保存
      const operationNote = [{
        operationNoteId: {
          sequenceNumber: emp.sequenceNumber,
          staffDailyCleaning: staff.staffDailyCleaning,
          cdSite: emp.cdSite,
          day,
        },
        cdContract: emp.cdContract,
        note
      }]

      this.http.post(
        API_BASE_URL + '/api/v1/operationNote/save?days=' + [day], operationNote, httpOptions
      ).subscribe(res => {
        this.nowLoading = false;
      },error => {
        this.nowLoading = false;
        alert("メモの保存に失敗しました（休み登録は完了しています）");
        return;
      });
    },error => {
      this.nowLoading = false;
      alert("休み登録に失敗しました");
      return;
    });
  }

  /**
   * 休み登録の有無を取得する
   * 休み登録の直前に、他の担当者によってすでに登録されていないかを確認するために使う
   *
   * @param sequenceNumber 枝番
   * @param staffDailyCleaning 清掃員コード
   * @param cdSite 物件コード
   * @param day YYYY-MM-DD形式の日付
   * @return 登録済みの休み
   */
  async getStatus(sequenceNumber: string, staffDailyCleaning: string, cdSite: string, day: string) {
    const params = new HttpParams()
      .set('sequenceNumber', sequenceNumber)
      .set('staffDailyCleaning', staffDailyCleaning)
      .set('cdSite', cdSite)
      .set('day', day);

    return await this.http.get(
      API_BASE_URL + '/api/v1/status/getStatusForDataConfirm',
      {headers: this.headers, params: params}
    ).toPromise();
  }

  /**
   * その日の勤務に休み登録が必要かを判定する
   *
   * @param emp 勤務（集計中の作業データ）
   * @param day YYYY-MM-DD形式の日付
   * @return 'op'：代勤とかぶっているので必要／'holi'：他の行に休み登録があるので要確認／''：指摘なし
   */
  private judgeNeedHoliday(emp: any, day: string): string {
    // その日に勤務がなければ、そもそも休み登録は不要
    if (!emp.cellIndex[day]) {
      return '';
    }
    if (emp.ops.some(op => (op.cellIndex[day] || []).length)) {
      return 'op';
    }
    if (emp.holis.some(holi => (holi.cellIndex[day] || []).length)) {
      return 'holi';
    }
    return '';
  }

  /**
   * 稼働管理のURLを作る
   * ルート：operations/:hasHoliday/:start/:end/:staffCd
   * 期間はその清掃員で確認が必要な日の最初～最後、hasHolidayは「手配あり」を外すfalse固定
   *
   * @param staff 清掃員のまとまり
   * @return 稼働管理のURL
   */
  operationUrl(staff: StaffGroup): string {
    const columns = staff.columns;
    const start = columns.length ? columns[0].day : this.formatDate(this.startDate);
    const end = columns.length ? columns[columns.length - 1].day : this.formatDate(this.endDate);

    return '#/operations/false/' + start + '/' + end + '/' + staff.staffDailyCleaning;
  }

  /**
   * 日付列の見出しを作る
   *
   * @param day YYYY-MM-DD形式の日付
   * @return 7/21（火）形式の文字列
   */
  private formatDayLabel(day: string): string {
    const ymd = day.substring(0, 10).split('-');
    return Number(ymd[1]) + '/' + Number(ymd[2]) + '（' + this.getWeekDay(day) + '）';
  }

  /**
   * 値が入っているか判定
   */
  private hasValue(value: string): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  /**
   * 日付をYYYY-MM-DD形式の文字列に変換
   * Datepickerから渡されるDateと、input[type=date]から渡される文字列の両方に対応
   *
   * @param value 日付
   * @return YYYY-MM-DD形式の文字列
   */
  private formatDate(value: any): string {
    if (!value) {
      return '';
    }
    if (!(value instanceof Date)) {
      return String(value);
    }
    const month = ('0' + (value.getMonth() + 1)).slice(-2);
    const day = ('0' + value.getDate()).slice(-2);
    return value.getFullYear() + '-' + month + '-' + day;
  }

  /**
   * 閉じるボタンイベント
   */
  close() {
    this.dialogRef.close();
  }
}
